/**
 * 브랜드 색 완화·파생 유틸
 * - CI 원색을 그대로 쓰지 않고 HSL 클램프로 눈 피로도를 낮춘 brandBase 생성
 * - brandBase에서 primary / light / grid 선택색 등 역할 슬롯 자동 파생
 */

/** @typedef {{ h: number, s: number, l: number }} Hsl */
/** @typedef {{ r: number, g: number, b: number }} Rgb */

/**
 * @param {string} hex
 * @returns {Rgb | null}
 */
export function hexToRgb(hex) {
  const raw = String(hex || '').trim().replace(/^#/, '');
  if (!/^[0-9a-fA-F]{6}$/.test(raw) && !/^[0-9a-fA-F]{3}$/.test(raw)) return null;
  const full =
    raw.length === 3
      ? raw
          .split('')
          .map((c) => c + c)
          .join('')
      : raw;
  const n = parseInt(full, 16);
  return { r: (n >> 16) & 255, g: (n >> 8) & 255, b: n & 255 };
}

/**
 * @param {number} r
 * @param {number} g
 * @param {number} b
 * @returns {string}
 */
export function rgbToHex(r, g, b) {
  const clamp = (v) => Math.max(0, Math.min(255, Math.round(v)));
  return (
    '#' +
    ((1 << 24) + (clamp(r) << 16) + (clamp(g) << 8) + clamp(b)).toString(16).slice(1).toUpperCase()
  );
}

/**
 * @param {Rgb} rgb
 * @returns {Hsl}
 */
export function rgbToHsl({ r, g, b }) {
  const rn = r / 255;
  const gn = g / 255;
  const bn = b / 255;
  const max = Math.max(rn, gn, bn);
  const min = Math.min(rn, gn, bn);
  const l = (max + min) / 2;
  if (max === min) return { h: 0, s: 0, l: l * 100 };
  const d = max - min;
  const s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
  let h = 0;
  if (max === rn) h = ((gn - bn) / d + (gn < bn ? 6 : 0)) / 6;
  else if (max === gn) h = ((bn - rn) / d + 2) / 6;
  else h = ((rn - gn) / d + 4) / 6;
  return { h: h * 360, s: s * 100, l: l * 100 };
}

/**
 * @param {Hsl} hsl
 * @returns {Rgb}
 */
export function hslToRgb({ h, s, l }) {
  const hh = ((h % 360) + 360) % 360;
  const ss = Math.max(0, Math.min(100, s)) / 100;
  const ll = Math.max(0, Math.min(100, l)) / 100;
  if (ss === 0) {
    const v = Math.round(ll * 255);
    return { r: v, g: v, b: v };
  }
  const hue2rgb = (p, q, t) => {
    let tt = t;
    if (tt < 0) tt += 1;
    if (tt > 1) tt -= 1;
    if (tt < 1 / 6) return p + (q - p) * 6 * tt;
    if (tt < 1 / 2) return q;
    if (tt < 2 / 3) return p + (q - p) * (2 / 3 - tt) * 6;
    return p;
  };
  const q = ll < 0.5 ? ll * (1 + ss) : ll + ss - ll * ss;
  const p = 2 * ll - q;
  const hk = hh / 360;
  return {
    r: Math.round(hue2rgb(p, q, hk + 1 / 3) * 255),
    g: Math.round(hue2rgb(p, q, hk) * 255),
    b: Math.round(hue2rgb(p, q, hk - 1 / 3) * 255),
  };
}

/**
 * @param {string} hex
 * @returns {string}
 */
export function normalizeHex(hex) {
  const rgb = hexToRgb(hex);
  if (!rgb) return '';
  return rgbToHex(rgb.r, rgb.g, rgb.b);
}

/**
 * @param {string} hex
 * @returns {number} 0~255 상대 밝기
 */
export function relativeBrightness(hex) {
  const rgb = hexToRgb(hex);
  if (!rgb) return 128;
  return (rgb.r * 299 + rgb.g * 587 + rgb.b * 114) / 1000;
}

/**
 * 배경 대비용 전경색 (흰/검)
 * @param {string} bgHex
 * @returns {string}
 */
export function contrastFg(bgHex) {
  return relativeBrightness(bgHex) > 150 ? '#111827' : '#FFFFFF';
}

/**
 * WCAG 상대 휘도
 * @param {string} hex
 * @returns {number}
 */
export function relativeLuminance(hex) {
  const rgb = hexToRgb(hex);
  if (!rgb) return 0;
  const lin = (c) => {
    const s = c / 255;
    return s <= 0.03928 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4;
  };
  return 0.2126 * lin(rgb.r) + 0.7152 * lin(rgb.g) + 0.0722 * lin(rgb.b);
}

/**
 * @param {string} a
 * @param {string} b
 * @returns {number}
 */
export function contrastRatio(a, b) {
  const L1 = relativeLuminance(a);
  const L2 = relativeLuminance(b);
  const lighter = Math.max(L1, L2);
  const darker = Math.min(L1, L2);
  return (lighter + 0.05) / (darker + 0.05);
}

/**
 * CI 원색 → ERP용 soft brand (채도·명도 클램프)
 * @param {string} hex
 * @param {{ maxS?: number, minL?: number, maxL?: number }} [opts]
 * @returns {{ brandBase: string, softened: boolean, sourceHex: string }}
 */
export function softenBrand(hex, opts = {}) {
  const maxS = opts.maxS ?? 58;
  const minL = opts.minL ?? 38;
  const maxL = opts.maxL ?? 52;
  const sourceHex = normalizeHex(hex) || '#2563EB';
  const rgb = hexToRgb(sourceHex);
  const hsl = rgbToHsl(rgb);
  let softened = false;
  let { h, s, l } = hsl;
  if (s > maxS) {
    s = maxS;
    softened = true;
  }
  if (l < minL) {
    l = minL;
    softened = true;
  }
  if (l > maxL) {
    l = maxL;
    softened = true;
  }
  // 극단적 채도 원색(순수 빨강/초록 등)은 한 단계 더 낮춤
  if (hsl.s >= 90 && hsl.l >= 40 && hsl.l <= 60) {
    s = Math.min(s, 48);
    softened = true;
  }
  const out = hslToRgb({ h, s, l });
  return {
    brandBase: rgbToHex(out.r, out.g, out.b),
    softened,
    sourceHex,
  };
}

/**
 * HSL 조정 후 hex
 * @param {string} hex
 * @param {{ dS?: number, dL?: number, s?: number, l?: number }} adj
 */
export function adjustHsl(hex, adj = {}) {
  const rgb = hexToRgb(hex);
  if (!rgb) return hex;
  const hsl = rgbToHsl(rgb);
  const next = {
    h: hsl.h,
    s: adj.s != null ? adj.s : hsl.s + (adj.dS || 0),
    l: adj.l != null ? adj.l : hsl.l + (adj.dL || 0),
  };
  const out = hslToRgb(next);
  return rgbToHex(out.r, out.g, out.b);
}

/**
 * rgba 문자열
 * @param {string} hex
 * @param {number} alpha 0~1
 */
export function hexToRgba(hex, alpha) {
  const rgb = hexToRgb(hex);
  if (!rgb) return `rgba(37, 99, 235, ${alpha})`;
  return `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, ${alpha})`;
}

/**
 * rgba/rgb 문자열 → #RRGGBB (color input용)
 * @param {string} color
 * @returns {string|null}
 */
export function rgbaStringToHex(color) {
  const s = String(color || '').trim();
  if (!s) return null;
  const hex = normalizeHex(s);
  if (hex) return hex;
  const m = s.match(
    /^rgba?\(\s*(\d{1,3})\s*,\s*(\d{1,3})\s*,\s*(\d{1,3})(?:\s*,\s*([0-9.]+))?\s*\)$/i
  );
  if (!m) return null;
  return rgbToHex(Number(m[1]), Number(m[2]), Number(m[3]));
}

/**
 * rgba 알파 추출 (없으면 null)
 * @param {string} color
 * @returns {number|null}
 */
export function parseRgbaAlpha(color) {
  const s = String(color || '').trim();
  const m = s.match(
    /^rgba\(\s*\d{1,3}\s*,\s*\d{1,3}\s*,\s*\d{1,3}\s*,\s*([0-9.]+)\s*\)$/i
  );
  if (!m) return null;
  const a = Number(m[1]);
  if (Number.isNaN(a)) return null;
  return Math.min(1, Math.max(0, a));
}

/**
 * color input / 슬롯 표시용 안전한 hex
 * @param {string} color
 * @param {string} [fallback='#2563EB']
 */
export function colorToPickerHex(color, fallback = '#2563EB') {
  return rgbaStringToHex(color) || normalizeHex(color) || fallback;
}

/**
 * 활성 헤더 정렬 아이콘 — 배경과 대비되는 강조색
 * @param {string} headerBg
 */
export function deriveHeaderSort(headerBg) {
  const yellow = '#FDE047';
  const amber = '#F59E0B';
  const white = '#FFFFFF';
  const candidates = [yellow, amber, white, '#111827'];
  let best = yellow;
  let bestRatio = 0;
  candidates.forEach((c) => {
    const r = contrastRatio(c, headerBg);
    if (r > bestRatio) {
      bestRatio = r;
      best = c;
    }
  });
  return best;
}

/**
 * brandBase에서 역할 슬롯 자동 파생
 * @param {string} brandBase
 * @returns {Record<string, string>}
 */
export function deriveBrandSlots(brandBase) {
  const base = normalizeHex(brandBase) || '#2563EB';
  const primary = base;
  const primaryDark = adjustHsl(base, { dL: -12, dS: 4 });
  const primaryLight = adjustHsl(base, { s: 75, l: 92 });
  const focusRing = adjustHsl(base, { s: 70, l: 78 });
  const rowHover = adjustHsl(base, { s: 55, l: 96 });
  const rowSelected = primaryLight;
  const headerActiveBg = primary;
  const headerActiveHover = adjustHsl(base, { dL: -18, dS: 6 });
  const headerActiveFg = contrastFg(headerActiveBg);
  const headerActiveSort = deriveHeaderSort(headerActiveBg);

  return {
    primary,
    primaryDark,
    primaryLight,
    tabHoverActive: primaryLight,
    focusRing,
    gridHeaderActive: headerActiveBg,
    gridHeaderActiveHover: headerActiveHover,
    gridHeaderActiveFg: headerActiveFg,
    gridHeaderActiveSort: headerActiveSort,
    gridHeaderAccent: primary,
    gridRowSelected: rowSelected,
    gridRowHover: rowHover,
    gridCellSelected: hexToRgba(primary, 0.28),
    /** 블럭 외곽 — 선택 행(연한 파랑)과 대비되는 앰버 */
    gridCellBlockBorder: '#D97706',
    gridCellFocused: primary,
    textLink: primary,
    statusInfo: primary,
  };
}

/**
 * 가산/감산 혼합 (CI 다중색 → 대표 메인 후보, Color Mixer 이식)
 * @param {{ hex: string, weight: number }[]} colors
 * @param {'additive'|'subtractive'} mode
 * @returns {string}
 */
export function mixColors(colors, mode = 'additive') {
  const list = (colors || []).filter((c) => hexToRgb(c.hex) && c.weight > 0);
  if (!list.length) return '#2563EB';
  const total = list.reduce((s, c) => s + Number(c.weight) || 0, 0) || 1;

  if (mode === 'additive') {
    let sumR2 = 0;
    let sumG2 = 0;
    let sumB2 = 0;
    list.forEach((c) => {
      const rgb = hexToRgb(c.hex);
      const w = Number(c.weight) / total;
      sumR2 += rgb.r * rgb.r * w;
      sumG2 += rgb.g * rgb.g * w;
      sumB2 += rgb.b * rgb.b * w;
    });
    return rgbToHex(Math.sqrt(sumR2), Math.sqrt(sumG2), Math.sqrt(sumB2));
  }

  let totalC = 0;
  let totalM = 0;
  let totalY = 0;
  let totalK = 0;
  list.forEach((c) => {
    const rgb = hexToRgb(c.hex);
    const w = Number(c.weight) / total;
    const r = rgb.r / 255;
    const g = rgb.g / 255;
    const b = rgb.b / 255;
    const k = 1 - Math.max(r, g, b);
    const cmykC = k === 1 ? 0 : (1 - r - k) / (1 - k);
    const cmykM = k === 1 ? 0 : (1 - g - k) / (1 - k);
    const cmykY = k === 1 ? 0 : (1 - b - k) / (1 - k);
    totalC += cmykC * w;
    totalM += cmykM * w;
    totalY += cmykY * w;
    totalK += k * w;
  });
  return rgbToHex(
    255 * (1 - totalC) * (1 - totalK),
    255 * (1 - totalM) * (1 - totalK),
    255 * (1 - totalY) * (1 - totalK)
  );
}

/**
 * 추천 팔레트용: soft brand의 변형 메인들
 * @param {string} sourceHex
 * @returns {{ id: string, label: string, mainHex: string, description: string }[]}
 */
export function recommendMainPalettes(sourceHex) {
  const soft = softenBrand(sourceHex);
  const base = soft.brandBase;
  const hsl = rgbToHsl(hexToRgb(base));
  const calmRgb = hslToRgb({ h: (hsl.h + 25) % 360, s: Math.min(hsl.s, 42), l: 46 });
  const mutedRgb = hslToRgb({ h: hsl.h, s: Math.min(hsl.s, 35), l: 48 });
  const calm = softenBrand(rgbToHex(calmRgb.r, calmRgb.g, calmRgb.b), { maxS: 45 });
  const muted = softenBrand(rgbToHex(mutedRgb.r, mutedRgb.g, mutedRgb.b), { maxS: 38 });
  return [
    {
      id: 'soft-brand',
      label: '소프트 브랜드',
      mainHex: soft.brandBase,
      description: soft.softened ? '원본 CI를 완화한 기본 추천' : '입력색 기반 기본 추천',
    },
    {
      id: 'calm-shift',
      label: '캄 시프트',
      mainHex: calm.brandBase,
      description: '채도를 낮추고 색상축을 살짝 이동',
    },
    {
      id: 'muted',
      label: '뮤티드',
      mainHex: muted.brandBase,
      description: '장시간 업무에 맞춘 낮은 채도',
    },
  ];
}
